/**
 * The upload heartbeat's schedule (AKL-896): while an upload is in progress the browser
 * tells the server every five minutes that it is still here, and once more when the tab
 * comes back into view, so the deployment's upload lock does not look abandoned between
 * files, during a pause or a stop, or after a laptop wakes. The request itself — the token
 * pre-flight, the interceptor flag, how each answer is classified — is
 * `heartbeatDeploymentUpload` in `deploymentsApi.tsx`; this module only decides *when* to
 * call it, and never touches the upload's own state.
 *
 * **A fixed interval, not a self-rescheduling timer.** The tick runs on `setInterval`
 * from the moment the schedule starts; a beat asked for on demand (the tab coming back)
 * neither moves the next tick nor is moved by it. What keeps the two from stacking is a
 * **minimum spacing**: whatever asked, a heartbeat goes out only when at least
 * `UPLOAD_HEARTBEAT_MIN_SPACING_MS` have passed since the last one was sent. The upload's
 * start counts as a beat — `start_upload` has just stamped the lock — so a tab switch in
 * the first minute sends nothing either. A tick that lands inside the spacing is skipped,
 * and the worst gap that allows is one of five and a half minutes, well inside the
 * server's steal window. One heartbeat is in flight at a time: a tick or a beat while the
 * previous one is unanswered is skipped, not queued.
 *
 * **Refusals stop it; transient failures do not.** The endpoint may not be deployed yet
 * (every beat answers 404), the lock may have been taken over or expired (403), or the
 * deployment may be gone (400): after `UPLOAD_HEARTBEAT_MAX_CONSECUTIVE_REFUSALS` such
 * answers in a row, the schedule stands down for the rest of the upload rather than pinging
 * every five minutes for hours — each landed file still bumps the lock, exactly as before
 * this change. A transient failure (the server erred with a 5xx, the network is down,
 * Keycloak could not be reached) does not count: the server has refused nothing, and the
 * manager's own loop is already waiting for `online`. An accepted heartbeat clears the
 * count. Once stopped, the schedule stays stopped; a new schedule starts when the next
 * upload is accepted.
 *
 * **It starts only once this client holds the lock.** The provider creates the schedule
 * when the server has accepted `start_upload` (`onUploadStarted`), not when the upload is
 * merely in progress: before that there is no lock to keep alive, every beat would be a
 * 403, and three of them would have stopped a schedule the lock — acquired later by a
 * Resume — then needed (review cycle 1).
 */

import {
  heartbeatDeploymentUpload,
  UploadHeartbeatOutcome,
} from "../../api/deploymentsApi";

/** How often a heartbeat is sent while an upload is in progress. */
export const UPLOAD_HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

/**
 * The least time between two heartbeats, whatever asked for the second — a tick moments
 * after the tab came back, or the tab coming back moments after a tick.
 */
export const UPLOAD_HEARTBEAT_MIN_SPACING_MS = 60 * 1000;

/**
 * Refusals in a row after which the schedule stops for the rest of the upload. Transient
 * failures do not count; an accepted heartbeat resets it.
 */
export const UPLOAD_HEARTBEAT_MAX_CONSECUTIVE_REFUSALS = 3;

/** Sends one heartbeat for a deployment and reports how it went; never rejects by contract. */
export type SendUploadHeartbeat = (
  deploymentId: number,
) => Promise<UploadHeartbeatOutcome>;

/** A running heartbeat schedule; see `startUploadHeartbeat`. */
export type UploadHeartbeat = {
  /**
   * Asks for a heartbeat now — the tab has come back into view. Sent only when the
   * schedule is running, nothing is in flight and the minimum spacing has passed.
   */
  beat: () => void;
  /** Ends the schedule: the upload is over, or the provider is going away. */
  stop: () => void;
};

/**
 * Starts the heartbeat schedule for the deployment whose upload lock this client holds —
 * so it is started once the server has accepted `start_upload`, not before. The first beat
 * is one interval in: the lock has just been stamped. The caller stops it when the upload
 * ends, however it ends.
 * @param deploymentId the deployment being uploaded to
 * @param send how a heartbeat is sent; a test seam — production callers take the default
 * @returns the running schedule
 */
export const startUploadHeartbeat = (
  deploymentId: number,
  send: SendUploadHeartbeat = heartbeatDeploymentUpload,
): UploadHeartbeat => {
  let stopped = false;
  let inFlight = false;
  let lastSentAt = Date.now();
  let consecutiveRefusals = 0;
  let timer: ReturnType<typeof setInterval> | undefined;

  const stop = () => {
    stopped = true;
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };

  const beat = () => {
    if (stopped || inFlight) {
      return;
    }
    const now = Date.now();
    if (now - lastSentAt < UPLOAD_HEARTBEAT_MIN_SPACING_MS) {
      return;
    }
    lastSentAt = now;
    inFlight = true;
    send(deploymentId)
      .then((outcome) => {
        if (outcome === "accepted") {
          consecutiveRefusals = 0;
        } else if (outcome === "refused") {
          consecutiveRefusals += 1;
          if (
            consecutiveRefusals >= UPLOAD_HEARTBEAT_MAX_CONSECUTIVE_REFUSALS &&
            !stopped
          ) {
            console.warn(
              `Upload heartbeat for deployment ${deploymentId} stopped after ${consecutiveRefusals} refusals in a row; the lock is kept alive by each landed file, as before.`,
            );
            stop();
          }
        }
        // `transient`: the server refused nothing; the next beat asks again.
      })
      .catch((error) => {
        // Belt and braces — the sender never rejects by contract. Treated as transient.
        console.warn(
          `Upload heartbeat for deployment ${deploymentId} failed unexpectedly:`,
          error,
        );
      })
      .finally(() => {
        inFlight = false;
      });
  };

  timer = setInterval(beat, UPLOAD_HEARTBEAT_INTERVAL_MS);
  return { beat, stop };
};
